/**
 * Sentry initialization for the frontend.
 *
 * Mirrors the backend Sentry setup pattern (server.py:113-128):
 *  - DSN read from env (REACT_APP_SENTRY_DSN)
 *  - 10% performance trace sampling
 *  - Environment-tagged so preview vs production errors are separable
 *
 * Inbound filters reject environmental noise that is NOT a real bug:
 *  - GA / Google Analytics blocked by Safari ITP / Private Relay
 *  - Cloudflare RUM beacon failures
 *  - Browser extension errors (chrome-extension://, moz-extension://)
 *  - ResizeObserver loop warnings (benign Chrome quirk)
 *  - Network errors on bot/crawler user-agents
 */

import * as Sentry from '@sentry/react';

const NOISE_PATTERNS = [
  // Google Analytics blocked by browser privacy
  'google-analytics.com',
  'googletagmanager.com',
  'gtag is not defined',
  // Cloudflare RUM / insights
  'cloudflareinsights.com',
  'cdn-cgi/rum',
  // Browser extension noise
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  // Benign quirks
  'ResizeObserver loop',
  'Non-Error promise rejection captured',
  // Network drops on unfocused / backgrounded tabs
  'NetworkError when attempting to fetch resource',
  'Load failed',
];

function shouldIgnoreEvent(event, hint) {
  // 1) Drop events from known-noise sources
  const message = (event?.message || hint?.originalException?.message || '').toLowerCase();
  if (NOISE_PATTERNS.some((p) => message.includes(p.toLowerCase()))) return true;

  // 2) Drop events caused by URLs that match noise patterns
  const exception = event?.exception?.values?.[0];
  const frames = exception?.stacktrace?.frames || [];
  const hasNoiseFrame = frames.some((f) => {
    const filename = (f.filename || '').toLowerCase();
    return NOISE_PATTERNS.some((p) => filename.includes(p.toLowerCase()));
  });
  if (hasNoiseFrame) return true;

  // 3) Drop bot user-agent traffic (we don't need to debug Googlebot's errors)
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/bot|crawl|spider|headlesschrome|puppeteer|playwright|lighthouse/i.test(ua)) return true;

  return false;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Silent skip if not configured (e.g., local dev without DSN)
    return;
  }

  // Auto-detect environment (preview vs production) the same way the backend does.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPreview = host.includes('preview.emergentagent') || host.includes('localhost') || host === '127.0.0.1';
  const environment = isPreview ? 'preview' : 'production';

  Sentry.init({
    dsn,
    environment,
    // Keep this low — Sentry free tier event budget is precious.
    tracesSampleRate: 0.1,
    // Anonymous user context (no PII) — use the same anonymous session id
    // already generated by Journal analytics so we can correlate frontend
    // errors to view events without identifying anyone personally.
    beforeSend(event, hint) {
      if (shouldIgnoreEvent(event, hint)) return null;
      try {
        const sid = localStorage.getItem('kgob_journal_sid');
        if (sid) {
          event.user = { id: sid };
        }
      } catch (e) {
        // localStorage may be blocked in private mode — ignore.
      }
      return event;
    },
    // Tag every event with a release marker for grouping by deploy.
    release: process.env.REACT_APP_VERSION || 'kgob-frontend@1.0.0',
  });
}
